import React, { useMemo } from 'react';
import { noop } from 'lodash';
import classNames from 'classnames';
import { bool, func, object, string } from 'prop-types';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { ActionCtas } from '@fiverr-private/gig_page_nav';
import { Container, Divider, Stack } from '@fiverr-private/layout_components';
import { CustomerProtectionBanner } from '@fiverr-private/gig_page_banners';
import StudioBox from '../../StudioBox';
import PackagesTabs from '../../PackagesTabs';
import CollectWrapper from '../../TopNav/CollectWrapper';
import PayLaterMessage from '../../PayLaterMessage/PayLaterMessage';
import { ContactHourlySellerCard } from '../../ContactHourlySellerCard';
import { shouldRenderContactSeller } from '../../ContactSeller/utils';
import { ContactSeller } from '../../ContactSeller/ContactSeller';
import { SidebarHighlights } from './SidebarHighlights';

import {
    contactWrapperClass,
    contactHourlySellerWrapperClass,
    compactBannersWrapperClass,
} from './SidebarContent.ve.css';

const SidebarContent = ({
    customOrder,
    useStickyLayout,
    className = '',
    setRef = noop,
    seller = {},
}) => {
    const { general, isBusinessLayout, isInCheetahsMobileGigPage } = useGigPageContext();
    const { isStudio } = general;

    const shouldShowHourlyIndications = !!seller?.hourlyRate;

    const shouldShowCustomerProtectionBanner = Boolean(useStickyLayout && isBusinessLayout && seller?.isPro);

    const shouldDisplayContactSeller = useMemo(
        () =>
            shouldRenderContactSeller({
                general,
                useStickyLayout,
            }),
        [general, useStickyLayout]
    );

    return (
        <aside className={classNames(className, 'sidebar-content')} ref={setRef}>
            {!isInCheetahsMobileGigPage && (
                <Container paddingBottom={{ md: "3" }}>
                    <ActionCtas CollectionCta={CollectWrapper} />
                </Container>
            )}

            {isStudio && <StudioBox />}

            <PackagesTabs />
            {isInCheetahsMobileGigPage && !shouldShowHourlyIndications && (
                <Divider hidden={{ default: false, sm: true }} borderColor="grey_800" margin="0" order="60" />
            )}
            {shouldShowHourlyIndications && (
                <ContactHourlySellerCard className={classNames(contactHourlySellerWrapperClass, 'contact-hourly-seller-card')} />
            )}

            {!shouldShowHourlyIndications && (
                <>
                    <Stack
                        order={55}
                        className={contactWrapperClass}
                        borderRadius="xl"
                        direction="column"
                        wrap="nowrap"
                    >
                        {shouldDisplayContactSeller && <ContactSeller customOrder={customOrder} />}
                        <SidebarHighlights shouldUseStickyLayout={useStickyLayout} />
                    </Stack>

                    <PayLaterMessage />

                    <Container
                        hidden={{
                            default: true,
                            md: !shouldShowCustomerProtectionBanner,
                        }}
                    >
                        <CustomerProtectionBanner />
                    </Container>
                </>
            )}

            {shouldShowHourlyIndications && (
                <>
                    <Banners shouldShowCustomerProtectionBanner={shouldShowCustomerProtectionBanner} />
                    <PayLaterMessage />
                </>
            )}
        </aside>
    );
};

SidebarContent.propTypes = {
    customOrder: object,
    setRef: func,
    useStickyLayout: bool,
    seller: object,
    className: string,
};

export const Banners = ({ shouldShowCustomerProtectionBanner }) => (
    <Stack
        hidden={{ default: true, md: false }}
        direction="column"
        gap="0"
        className={compactBannersWrapperClass}
    >
        {shouldShowCustomerProtectionBanner && <CustomerProtectionBanner isCollapsible />}
    </Stack>
);

Banners.propTypes = {
    shouldShowCustomerProtectionBanner: bool,
};

export default SidebarContent;
